// ─── How it works — page ──────────────────────────────────
// Tài liệu hướng dẫn toàn diện về cách hệ thống vận hành.
// Bố cục: sidebar điều hướng (sticky) + nội dung cuộn theo từng section.

import { useState, useCallback } from 'react';
import {
  OverviewSection,
  ArchitectureSection,
  RolesSection,
  PagesSection,
} from './components/OverviewSections';
import { EnginesSection, ChinBotSection, CopyTradeFlowSection, TrailingStopFlowSection } from './components/EngineSections';
import { ChinPlayFlowSection } from './components/ChinPlayFlowSection';
import {
  DataFlowSection,
  RiskSection,
  TelegramSection,
  GuideSection,
} from './components/FlowGuideSections';

const NAV_ITEMS = [
  { id: 'overview', label: 'Tổng quan', icon: 'ri-compass-3-line' },
  { id: 'architecture', label: 'Kiến trúc', icon: 'ri-stack-line' },
  { id: 'roles', label: 'Phân quyền', icon: 'ri-shield-keyhole-line' },
  { id: 'pages', label: 'Các trang', icon: 'ri-layout-grid-line' },
  { id: 'engines', label: 'Các Engine', icon: 'ri-cpu-line' },
  { id: 'copy-trade-flow', label: 'Copy Trade', icon: 'ri-swap-line' },
  { id: 'trailing-stop-flow', label: 'Trailing Stop', icon: 'ri-shield-check-line' },
  { id: 'chin-bot', label: 'Chin Bot', icon: 'ri-flashlight-line' },
  { id: 'chin-play', label: 'Play Off/Order', icon: 'ri-play-list-line' },
  { id: 'data-flow', label: 'Luồng dữ liệu', icon: 'ri-flow-chart' },
  { id: 'risk', label: 'Quản lý rủi ro', icon: 'ri-shield-check-line' },
  { id: 'telegram', label: 'Telegram', icon: 'ri-telegram-line' },
  { id: 'guide', label: 'Hướng dẫn', icon: 'ri-map-pin-2-line' },
];

export default function HowItWorks() {
  const [activeId, setActiveId] = useState('overview');

  const scrollTo = useCallback((id: string) => {
    setActiveId(id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div className="px-4 md:px-6 py-4 md:py-6">
      {/* ─── Hero ─── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500/10 via-background-100 to-accent-500/10 border border-background-200/70 px-6 py-8 md:px-10 md:py-10 mb-6">
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-book-open-line text-background-50 text-sm w-5 h-5 flex items-center justify-center"></i>
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-primary-400">Tài liệu vận hành</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight">
            How it works — cách hệ thống vận hành
          </h1>
          <p className="text-foreground-600 text-sm mt-2 leading-relaxed">
            Từ cách kết nối cTrader, cách các engine phân tích thị trường, cho tới cách tín hiệu được gửi đi và lệnh được đặt.
            Bản hướng dẫn này giúp bạn hiểu trọn vẹn mọi tính năng và cơ chế của nền tảng.
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* ─── Sidebar navigation ─── */}
        <aside className="w-full lg:w-60 shrink-0">
          <nav className="lg:sticky lg:top-4 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-1 lg:pb-0">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollTo(item.id)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-all cursor-pointer ${
                  activeId === item.id
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/50'
                }`}
              >
                <i className={`${item.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* ─── Content ─── */}
        <main className="flex-1 min-w-0 space-y-14">
          <OverviewSection />
          <ArchitectureSection />
          <RolesSection />
          <PagesSection />
          <EnginesSection />
          <CopyTradeFlowSection />
          <TrailingStopFlowSection />
          <ChinBotSection />
          <ChinPlayFlowSection />
          <DataFlowSection />
          <RiskSection />
          <TelegramSection />
          <GuideSection />

          <div className="border-t border-background-200/70 pt-6 pb-2 text-center">
            <p className="text-foreground-400 text-xs">
              Chín Hồng Mao — Automated Trading Platform
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}